import React, { useEffect, useState, useRef } from 'react';
import { X, GitBranch, ExternalLink, FolderKanban } from 'lucide-react';
import { Link } from 'react-router-dom';
import '../../component-styles/RecordPages.css';
import API_BASE from '../../utils/api.js';

export default function ProductFolioModal({ item, onClose }) {
  const userId = localStorage.getItem('userId');

  const [usedIn, setUsedIn] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const attemptedBackfill = useRef(false);
  const [openProjects, setOpenProjects] = useState({});

  const fetchFolio = async () => {
    if (!item?.itemId || !userId) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${API_BASE}/api/products/${encodeURIComponent(item.itemId)}/folio?userId=${encodeURIComponent(
          userId
        )}`
      );
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`Folio request failed (${res.status})${text ? `: ${text}` : ''}`);
      }
      const data = await res.json();
      setUsedIn(Array.isArray(data.usedIn) ? data.usedIn : []);
      setProjects(Array.isArray(data.projects) ? data.projects : []);
      return { usedIn: Array.isArray(data.usedIn) ? data.usedIn : [] };
    } catch (err) {
      console.error('Failed to load product folio:', err);
      setError(err.message || 'Failed to load usage.');
      setUsedIn([]);
      setProjects([]);
      return { usedIn: [] };
    } finally {
      setLoading(false);
    }
  };

  const runBackfill = async () => {
    if (!item?.itemId || !userId) return;
    setSyncing(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/products/${encodeURIComponent(item.itemId)}/folio/backfill`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId }),
        }
      );
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`Backfill failed (${res.status})${text ? `: ${text}` : ''}`);
      }
    } catch (err) {
      console.error('Failed to backfill product usage:', err);
      setError(err.message || 'Failed to sync usage.');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    attemptedBackfill.current = false;
    (async () => {
      const res = await fetchFolio();
      if (!attemptedBackfill.current && res?.usedIn?.length === 0) {
        attemptedBackfill.current = true;
        await runBackfill();
        await fetchFolio();
      }
    })();
  }, [API_BASE, item, userId]);

  if (!item) return null;

  const groupedByProject = usedIn.reduce((acc, ref) => {
    const key = ref.projectId || 'unassigned';
    if (!acc[key]) {
      acc[key] = {
        projectId: ref.projectId || '',
        projectName: ref.projectName || 'Unassigned',
        items: [],
      };
    }
    acc[key].items.push(ref);
    return acc;
  }, {});
  const groupedList = Object.values(groupedByProject);
  const toggleProject = (projectId) => {
    setOpenProjects((prev) => ({ ...prev, [projectId]: !prev[projectId] }));
  };

  return (
    <div className="folio-overlay" onClick={onClose}>
      <div className="folio-modal" onClick={(e) => e.stopPropagation()}>
        <button className="folio-close" onClick={onClose}>
          <X size={20} />
        </button>

        <h3 className="folio-title">
          {item.name}
          {item.category && <span className="folio-sub">{item.category}</span>}
        </h3>

        <div className="folio-summary">
          <div>
            <label>Trade</label>
            <div>${Number(item.tradePrice ?? item.price ?? 0).toFixed(2)}</div>
          </div>

          <div>
            <label>Vendor</label>
            <div>{item.vendorName || item.vendor || 'â€”'}</div>
          </div>
        </div>

        <h4 className="folio-section-label">Projects</h4>
        <div className="folio-applied-list">
          {loading && <div className="folio-empty">Loading projectsâ€¦</div>}
          {!loading && error && <div className="folio-empty">{error}</div>}
          {!loading && projects.length === 0 && (
            <div className="folio-empty">No projects linked yet.</div>
          )}
          {!loading &&
            projects.map((p) => (
              <Link to={`/projects/${p.projectId}`} key={p.projectId} className="folio-applied-row">
                <FolderKanban size={16} className="folio-row-icon" />
                <div className="folio-row-text">
                  <b>{p.projectName || 'Project'}</b>
                </div>
                <ExternalLink size={14} className="folio-open-icon" />
              </Link>
            ))}
        </div>

        <h4 className="folio-section-label">Used In</h4>
        <div className="folio-applied-list">
          {loading && <div className="folio-empty">Loading usageâ€¦</div>}
          {!loading && error && <div className="folio-empty">{error}</div>}
          {!loading && usedIn.length === 0 && (
            <div className="folio-empty">Not used anywhere yet.</div>
          )}
          {!loading &&
            groupedList.map((group) => {
              const projectId = group.projectId || 'unassigned';
              const isOpen = openProjects[projectId] ?? group.items.length <= 3;
              return (
                <div
                  key={projectId}
                  style={{
                    border: '1px solid #ececec',
                    borderRadius: 10,
                    padding: 8,
                    background: '#fbfbfb',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => toggleProject(projectId)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                      background: 'transparent',
                      border: 'none',
                      padding: '6px 4px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      color: '#2f2f2f',
                    }}
                  >
                    <span>
                      {group.projectName || 'Project'}{' '}
                      <span style={{ color: '#888', fontWeight: 500 }}>({group.items.length})</span>
                    </span>
                    <span style={{ color: '#888', fontSize: 12 }}>{isOpen ? 'Hide' : 'Show'}</span>
                  </button>

                  {isOpen &&
                    group.items.map((ref, idx) => {
                      let url = '#';
                      const typeLabel = (ref.type || '').replace('-', ' ');

                      switch (ref.type) {
                        case 'proposal':
                          url = `/projects/${ref.projectId}/proposals/${ref.id}`;
                          break;
                        case 'invoice':
                          url = `/projects/${ref.projectId}/invoices/${ref.id}`;
                          break;
                        case 'purchase-order':
                          url = `/projects/${ref.projectId}/purchase-orders/${ref.id}`;
                          break;
                        default:
                          break;
                      }

                      return (
                        <Link
                          to={url}
                          key={`${projectId}-${ref.type}-${ref.id}-${idx}`}
                          className="folio-applied-row"
                        >
                          <GitBranch size={16} className="folio-row-icon" />

                          <div className="folio-row-text">
                            <b>{ref.label || 'Record'}</b>
                            {typeLabel ? ` - ${typeLabel}` : ''}
                          </div>

                          <ExternalLink size={14} className="folio-open-icon" />
                        </Link>
                      );
                    })}
                </div>
              );
            })}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button
            className="record-add-btn"
            type="button"
            onClick={async () => {
              await runBackfill();
              await fetchFolio();
            }}
            disabled={syncing}
          >
            {syncing ? 'Syncing…' : 'Sync Usage'}
          </button>
          <button className="record-add-btn" type="button" onClick={fetchFolio} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
}
